export type MaintenancePhase = 'normal' | 'scheduled' | 'winding_down' | 'maintenance';

export interface MaintenancePlatform {
  enabled: boolean;
  minVersion: string;
}

export interface MaintenanceResponse {
  phase: MaintenancePhase;
  platforms: {
    web: MaintenancePlatform;
    mobile: MaintenancePlatform;
  };
  startsAt: string | null;
  endsAt: string | null;
  serverTime: string;
  message: string | null;
  releaseNotesUrl: string | null;
  bypassToken: string | null;
}

export interface MaintenanceState extends MaintenanceResponse {
  /** Date.now() - Date.parse(serverTime) ao receber a resposta */
  clientServerOffsetMs: number;
  /** Date.now() no momento em que a resposta foi recebida */
  receivedAt: number;
}

/** Tempo "real" ajustado ao relógio do servidor */
export function trueNow(state: MaintenanceState): number {
  return Date.now() - state.clientServerOffsetMs;
}

export function shouldShowBanner(state: MaintenanceState): boolean {
  return state.phase === 'scheduled' || state.phase === 'winding_down';
}

export function shouldBlockRoute(state: MaintenanceState): boolean {
  return state.phase === 'winding_down' || state.phase === 'maintenance';
}

export function isMaintenance(state: MaintenanceState): boolean {
  return state.phase === 'maintenance';
}

/** Ms restantes até startsAt (para countdown do banner) */
export function msUntilStart(state: MaintenanceState): number {
  if (!state.startsAt) return 0;
  return Math.max(0, Date.parse(state.startsAt) - trueNow(state));
}

/** Ms restantes até endsAt (para countdown na página de manutenção) */
export function msUntilEnd(state: MaintenanceState): number {
  if (!state.endsAt) return 0;
  return Math.max(0, Date.parse(state.endsAt) - trueNow(state));
}

/** Formata duração em ms como "HH:MM:SS" ou "MM:SS" */
export function formatCountdown(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
}

/** Retorna hora absoluta no formato HH:MM a partir de uma ISO string */
export function formatAbsoluteTime(isoString: string | null): string {
  if (!isoString) return '--:--';
  const d = new Date(isoString);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Intervalo de polling em ms conforme a fase e proximidade da transição */
export function pollingIntervalMs(state: MaintenanceState): number {
  const now = trueNow(state);

  if (state.phase === 'maintenance') return 30_000;

  if (state.phase === 'winding_down') {
    if (state.startsAt && Date.parse(state.startsAt) - now < 5 * 60_000) return 15_000;
    return 15_000;
  }

  if (state.phase === 'scheduled') {
    if (state.startsAt && Date.parse(state.startsAt) - now < 5 * 60_000) return 15_000;
    return 60_000;
  }

  return 5 * 60_000; // normal
}
